/**
 * One of today's drops as a tile: its text mix — ★ proven texts, ✦ fresh AI
 * texts — or a lock when the fill may not touch it any more (sent, staged,
 * skipped). The ranks it takes are in the hover. The mini tile shows the
 * time only (the row beside it says the mix); the hover says the rest.
 * Tiles pop in one after the other and re-animate whenever the mix changes
 * (the parent re-keys them).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Lock, Pin, Sparkles, Star } from 'lucide-react';
import { DROP_GEN_DROP_KIND_WORDS } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { hhmm, type TodayDrop } from './kind-facts';

const TILE = 'relative flex min-w-0 items-center justify-center rounded-md border leading-none fill-mode-backwards animate-in fade-in-0 zoom-in-95 duration-300 motion-reduce:animate-none';

const LOCK_WORDS = { sent: 'already sent', staged: 'staged on SMSWiser', skipped: 'skipped today' } as const;

const STATE_CLASS = {
  locked: 'border-dashed border-border bg-muted/40 text-muted-foreground',
  proven: 'border-success/40 bg-success/10 text-success',
  mix: 'border-success/40 bg-success/10 text-foreground',
  ai: 'border-primary/30 bg-primary/5 text-primary',
  /** Phase 38 — a hit-again drop keeping the texts of the drop that hit. */
  copy: 'border-success/40 bg-success/5 text-success',
} as const;

const stateOf = (d: TodayDrop): keyof typeof STATE_CLASS => (d.locked ? 'locked' : d.copied ? 'copy' : d.mix.proven > 0 ? (d.mix.ai > 0 ? 'mix' : 'proven') : 'ai');

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** The hover: time · list · kind → what it sends (ranks, a short pool) · pinned. */
export function tileTitle(drop: TodayDrop): string {
  const { mix } = drop;
  const ranks = drop.ranks.length > 0 ? ` (${drop.ranks.map((r) => `#${r}`).join(', ')})` : '';
  const short = drop.short > 0 ? ` — only ${mix.proven} proven in the pool, ${drop.short} became AI` : '';
  const sends = drop.locked
    ? `${LOCK_WORDS[drop.locked]} — untouched`
    : drop.copied
      ? 'the texts of the drop that hit (no mix of its own)'
      : `${plural(mix.proven, 'proven text')}${ranks} + ${plural(mix.ai, 'fresh AI text')}${short}`;
  return `${hhmm(drop.minute)} · ${drop.listName ?? 'list'} · ${DROP_GEN_DROP_KIND_WORDS[drop.kind]} → ${sends}${drop.pinned ? ' · pinned by a person' : ''}`;
}

const Mix: FC<{ drop: TodayDrop }> = ({ drop: { mix } }) => (
  <span className="flex items-center gap-1 font-mono font-semibold">
    {mix.proven > 0 ? (
      <span className="flex items-center gap-0.5 text-success">
        <Star className="h-2.5 w-2.5" aria-hidden />
        {mix.proven}
      </span>
    ) : null}
    {mix.ai > 0 ? (
      <span className="flex items-center gap-0.5 text-primary">
        <Sparkles className="h-2.5 w-2.5" aria-hidden />
        {mix.ai}
      </span>
    ) : null}
  </span>
);

interface Props {
  drop: TodayDrop;
  index: number;
  showTime?: boolean;
  /** The small tile: the time (or the lock word) only. */
  mini?: boolean;
}

export const DropTile: FC<Props> = ({ drop, index, showTime, mini }) => {
  const state = stateOf(drop);
  return (
    <span style={{ animationDelay: `${index * 60}ms` }} title={tileTitle(drop)} data-testid="drop-tile" data-state={state} className={cn(TILE, STATE_CLASS[state], mini ? 'h-6 px-1.5 font-mono text-[10px] font-semibold' : 'h-11 flex-col gap-0.5 text-[10px]')}>
      {mini ? (
        <span>{drop.locked ?? hhmm(drop.minute)}</span>
      ) : (
        <>
          {drop.locked ? <span className="font-mono font-semibold">{drop.locked}</span> : drop.copied ? <span className="font-mono font-semibold">↻ copy</span> : <Mix drop={drop} />}
          {showTime ? <span className="font-mono text-[9px] text-muted-foreground">{hhmm(drop.minute)}</span> : null}
        </>
      )}
      {drop.pinned && !mini ? <Pin className="absolute right-0.5 top-0.5 h-2.5 w-2.5 text-muted-foreground" aria-hidden /> : null}
      {drop.locked && !mini ? <Lock className="absolute right-0.5 top-0.5 h-2.5 w-2.5" aria-hidden /> : null}
    </span>
  );
};
